/**
 * Utilitario para extrair mensagens de erro amigaveis em pt-BR.
 *
 * Prioridade:
 *  1. `err.response.data.message` (erros de API via axios)
 *  2. `err.response.data.errors[].message` (validacoes)
 *  3. `err.message` (filtrando ruido tipo "Request failed with status code ...")
 *  4. `String(err)` se for string
 *  5. Fallback generico
 */

const AXIOS_GENERIC_PREFIX = /^Request failed with status code\s+\d+/i;
const NETWORK_ERROR = /^Network Error$/i;

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/** Extrai `err.response.data.message`, já aparado, se for uma string não-vazia. */
function extractDataMessage(data: Record<string, unknown>): string | undefined {
  const msg = data.message;
  if (typeof msg === 'string' && msg.trim().length > 0) {
    return msg.trim();
  }
  return undefined;
}

/** Junta `err.response.data.errors[].message` (validação por campo) com ". ". */
function extractDataErrors(data: Record<string, unknown>): string | undefined {
  const errors = data.errors;
  if (!Array.isArray(errors) || errors.length === 0) return undefined;
  const joined = errors
    .map((e) => (isRecord(e) && typeof e.message === 'string' ? e.message : ''))
    .filter(Boolean)
    .join('. ');
  return joined || undefined;
}

/** Tenta extrair uma mensagem amigável do formato de erro do axios (response.data). */
function extractAxiosResponseMessage(err: Record<string, unknown>): string | undefined {
  const response = err.response;
  if (!isRecord(response)) return undefined;
  const data = response.data;
  if (!isRecord(data)) return undefined;
  return extractDataMessage(data) ?? extractDataErrors(data);
}

/** Normaliza `err.message` de um Error/AxiosError puro, filtrando ruído conhecido. */
function extractPlainMessage(err: Record<string, unknown>, fallback: string): string | undefined {
  const message = err.message;
  if (typeof message !== 'string') return undefined;

  const trimmed = message.trim();
  if (trimmed.length === 0) return fallback;
  if (AXIOS_GENERIC_PREFIX.test(trimmed)) return fallback;
  if (NETWORK_ERROR.test(trimmed)) return 'Falha de conexao. Verifique sua internet e tente novamente.';
  return trimmed;
}

export function getFriendlyError(err: unknown, fallback = 'Ocorreu um erro inesperado. Tente novamente.'): string {
  if (err == null) return fallback;

  if (typeof err === 'string') {
    const trimmed = err.trim();
    return trimmed.length > 0 ? trimmed : fallback;
  }

  if (!isRecord(err)) return fallback;

  return (
    extractAxiosResponseMessage(err) ??
    extractPlainMessage(err, fallback) ??
    fallback
  );
}

/**
 * Código de erro de domínio da API (`err.response.data.code`), quando houver.
 * Usado para traduzir respostas que pedem uma ação específica do usuário.
 */
export function getApiErrorCode(err: unknown): string | undefined {
  if (!isRecord(err)) return undefined;
  const response = err.response;
  if (!isRecord(response) || !isRecord(response.data)) return undefined;
  const code = response.data.code;
  return typeof code === 'string' ? code : undefined;
}

/** Conta já existente não entra na equipe sem aceite — vai por convite. */
export const USER_EXISTS_USE_INVITE = 'USER_EXISTS_USE_INVITE';

/**
 * Helper para detectar erros 403 (permissao negada). Retorna true se o status
 * HTTP for 403, tanto em AxiosError quanto em Error custom que preserve `status`.
 */
export function isForbiddenError(err: unknown): boolean {
  if (!isRecord(err)) return false;
  const response = (err as { response?: unknown }).response;
  if (isRecord(response)) {
    const status = (response as { status?: unknown }).status;
    if (status === 403) return true;
  }
  const direct = (err as { status?: unknown }).status;
  if (direct === 403) return true;
  return false;
}
